import type { one_to_one } from '@fireworkhq/proto-contract'
import { createSelector } from 'reselect'

import type { LiveHelperState } from './actions'

/** @returns The actions from the store. */
export function actionsSelector(state: LiveHelperState) {
  return state.actions
}

/**
 * @returns The visitor topic actions from the store.
 */
export function visitorPrivateActionsSelector(state: LiveHelperState) {
  return state.visitorPrivateActions
}

/** @returns The monitoring actions from the store. */
export function monitoringActionsSelector(state: LiveHelperState) {
  return state.monitoringActions
}

/** @returns The params from the store. */
export function paramsSelector(state: LiveHelperState) {
  return state.params
}

/** @returns The call data from the store. */
export function callDataSelector(state: LiveHelperState) {
  return state.callData
}

/** @returns Whether the visitor is on a call waiting for an agent (dialing) or with a agent (in a call). */
export function isVisitorOnCallSelector(state: LiveHelperState) {
  return !!state.callData?.id
}

/** @returns The client role. */
export function clientRoleSelector(state: LiveHelperState) {
  return state.clientRole
}

/** @returns The client controls. */
export function clientControlsSelector(state: LiveHelperState) {
  return state.clientControls
}

/** @returns The clients microphone muted state. */
export function microphoneMutedSelector(state: LiveHelperState) {
  return state.clientControls.microphoneMuted
}

/** @returns The clients camera enabled state. */
export function cameraEnabledSelector(state: LiveHelperState) {
  return state.clientControls.cameraEnabled
}

/** @returns Whether the visitor is actively being monitored. */
export function monitoringSelector(state: LiveHelperState) {
  return state.isMonitoring
}

/** @returns The widget visibility state. */
export function isWidgetVisibleSelector(state: LiveHelperState) {
  return state.isWidgetVisible
}

/** @returns The visibility rules match state. */
export function visibilityRulesMatchSelector(state: LiveHelperState) {
  return state.visibilityRulesMatch
}

/** @returns The waitlist capacity reached state. */
export function waitlistCapacityReachedSelector(state: LiveHelperState) {
  return state.waitlistCapacityReached
}

/**
 * @returns The call state of the UI.
 */
export function uiStateSelector(state: LiveHelperState) {
  return state.uiState.value
}

/**
 * @returns The embed instance id.
 */
export function embedInstanceIdSelector(state: LiveHelperState) {
  return state.embedInstanceId
}

/** @returns The active conversation. */
export function conversationSelector(state: LiveHelperState) {
  return state.conversation
}

/** @returns The previous active conversation. */
export function previousConversationSelector(state: LiveHelperState) {
  return state.previousConversation
}

/** @returns The text messages in a conversation. */
export function messagesSelector(state: LiveHelperState) {
  return state.messages
}

/**
 * @returns If we have any messages from the sales agent related to conversation
 * @param conversation
 */
export const hasSalesAgentMessagesInConversation =
  (conversation: one_to_one.shared.Conversation) => (state: LiveHelperState) =>
    !!state.messages.find(
      (message) =>
        message.conversation_id === conversation.id &&
        message.text_message?.author === 'AUTHOR_AGENT',
    )

/** @returns If the chat is waiting for visitor's response in a conversation. */
export function isWaitingForVisitorInputSelector(state: LiveHelperState) {
  if (state.messages.length === 0) {
    return false
  }

  const lastMessage = state.messages[state.messages.length - 1]
  if (lastMessage.type === 'post_sales_actions') {
    return true
  }

  return false
}

/** @returns Next cursor for fetching more messages */
export function nextMessagesCursorSelector(state: LiveHelperState) {
  return state.nextMessagesCursor
}

/** @returns Whether the visitor is streaming local audio/video tracks. */
export function isStreamingSelector(state: LiveHelperState) {
  return state.clientRole === 'host' && state.callData !== undefined
}

/** @returns The system errors in a conversation. */
export function liveHelperErrorsSelector(state: LiveHelperState) {
  return state.liveHelperErrors
}

/**
 * @returns Whether the visitor has sent any message in the current
 * conversation.
 */
export const hasSentMessageSelector = createSelector(
  messagesSelector,
  conversationSelector,
  (messages, conversation) => {
    if (!conversation?.id) {
      return false
    }

    // Check if the visitor has sent a message in the current conversation.
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      // Find a message from a different conversation. No need to look further.
      if (
        !!message.conversation_id &&
        message.conversation_id !== conversation.id
      ) {
        return false
      }
      if (message.text_message?.author === 'AUTHOR_VISITOR') {
        return true
      }
    }
  },
)

/** @returns If the agent is typing a message in the chat */
export function isAgentTypingSelector(state: LiveHelperState) {
  return state.isTyping
}

/** @returns Current position in queue */
export function queuePositionSelector(state: LiveHelperState) {
  return state.queuePosition
}

/** @returns The full screen preview attachment. */
export function fullScreenPreviewSelector(state: LiveHelperState) {
  return state.fullScreenPreview
}

/**  @returns Whether the recording statement should be displayed. */
export function displayRecordingStatementSelector(state: LiveHelperState) {
  return state.displayRecordingStatement
}

/** @returns Whether the idle state text was closed automatically */
export function isIdleStateTextClosedAutomaticallySelector(
  state: LiveHelperState,
) {
  return state.isIdleStateTextClosedAutomatically
}

/** @returns Whether the idle state text is eligible to be shown */
export function isIdleStateTextEligibleToShowSelector(state: LiveHelperState) {
  const lastDismissTime = state.idleStateTextDismissTime
  const wasDismissedInLast24Hours =
    lastDismissTime && Date.now() - lastDismissTime < 86400000
  return !state.isIdleStateTextClosedAutomatically && !wasDismissedInLast24Hours
}

/** @return Automated message from visitor join success */
export function automatedMessageSelector(state: LiveHelperState) {
  return state.automatedMessage
}

/** @returns Last time automated message was dismissed */
export function automatedMessageDismissTimeSelector(state: LiveHelperState) {
  return state.automatedMessageDismissTime
}

/** @returns Last time widget was manually closed */
export function closeWidgetTimeSelector(state: LiveHelperState) {
  return state.closeWidgetTime
}

/** @returns The redirect url from agent */
export function redirectUrlSelector(state: LiveHelperState) {
  return state.redirectUrl
}

/**
 * @returns The visitor information
 */
export function visitorInformationSelector(state: LiveHelperState) {
  return state.visitorInformation
}

/**
 * @returns Video call id for which camera access is allowed
 */
export function cameraAccessAllowedForVideoCallIdSelector(
  state: LiveHelperState,
) {
  return state.cameraAccessAllowedForVideoCallId
}

/**
 * @returns Whether the conversation is transferring to another agent
 */
export function isTransferringSelector(state: LiveHelperState) {
  return state.isTransferring
}
